/**
 * Locale Configuration
 *
 * This file controls which locales are publicly available for SEO indexing and hreflang tags.
 *
 * HOSTNAME-BASED HIDING:
 * - PRODUCTION (pos.toasttab.com): Hidden locales are filtered out (noindex, no hreflang, no UI)
 * - NON-PRODUCTION (staging, dev hostnames): Hidden locales are visible for testing
 * - PREVIEW MODE: Hidden locales are visible for testing purposes
 *
 * This allows safe testing on staging/dev environments while keeping production clean.
 * Bug fixes can be deployed without accidentally exposing hidden locales on production.
 *
 * TO LAUNCH ES-US LOCALE:
 * 1. Remove 'es-us' and 'es-US' from PRODUCTION_HIDDEN_LOCALES array below
 * 2. Deploy the changes
 * 3. The locale will automatically become discoverable and indexable on pos.toasttab.com
 *
 * This affects:
 * - robots meta tags (noindex/index)
 * - hreflang alternate links
 * - Region/language selectors
 * - All SEO components (main, otl)
 */

// Locales hidden only in production (visible in preview for testing)
export const PRODUCTION_HIDDEN_LOCALES: string[] = [
  // Add other pre-launch locales here
]

/**
 * Cached preview mode result - computed once at module load time
 * since environment variables don't change during runtime
 */
const IS_PREVIEW_MODE: boolean =
  process.env.ACTIVE_ENV === 'preview' || process.env.NODE_ENV === 'development'

/**
 * Check if we're running on production hostname (pos.toasttab.com)
 * This is used to hide locales specifically on production domain
 */
const isProductionHostname = (): boolean => {
  if (typeof window === 'undefined') {
    // During SSR, check environment variables
    return process.env.GATSBY_CMS_URL === 'https://pos.toasttab.com'
  }
  // During client-side, check actual hostname
  return window.location.hostname === 'pos.toasttab.com'
}

/**
 * Check if we're in preview mode (cached result)
 */
const isPreviewMode = (): boolean => IS_PREVIEW_MODE

/**
 * Check if a locale should be hidden from UI elements (region selectors, navigation)
 * In preview mode, no locales are hidden for testing UI functionality
 * On production hostname (pos.toasttab.com), PRODUCTION_HIDDEN_LOCALES are hidden
 * On non-production hostnames (staging, dev), locales are visible for testing
 */
export const isLocaleHidden = (locale?: string): boolean => {
  if (!locale) return false

  // In preview mode, don't hide any locales from UI for testing
  if (isPreviewMode()) return false

  // Only hide locales on production hostname (pos.toasttab.com)
  if (!isProductionHostname()) return false
  // On production hostname, hide locales from PRODUCTION_HIDDEN_LOCALES
  return PRODUCTION_HIDDEN_LOCALES.some((hiddenLocale) =>
    locale.includes(hiddenLocale)
  )
}

/**
 * Check if a locale should be hidden from SEO (robots meta, hreflang)
 * Hidden locales are always hidden from SEO in both preview and production
 * to prevent search engine indexing of unreleased content
 */
export const isLocaleHiddenFromSEO = (locale?: string): boolean => {
  if (!locale) return false

  // Only hide locales on production hostname (pos.toasttab.com)
  if (!isProductionHostname()) return false

  // Always hide from SEO in both preview and production
  return PRODUCTION_HIDDEN_LOCALES.some((hiddenLocale) =>
    locale.includes(hiddenLocale)
  )
}

/**
 * Check if a URL contains a hidden locale (for UI filtering)
 * In preview mode, no URLs are hidden for testing purposes
 * On production hostname (pos.toasttab.com), URLs with PRODUCTION_HIDDEN_LOCALES are hidden
 * On non-production hostnames (staging, dev), URLs are visible for testing
 */
export const isUrlHidden = (url?: string): boolean => {
  if (!url) return false

  // In preview mode, don't hide any URLs from UI
  if (isPreviewMode()) return false

  // Only hide URLs on production hostname (pos.toasttab.com)
  if (!isProductionHostname()) return false

  // On production hostname, hide URLs with hidden locales
  return PRODUCTION_HIDDEN_LOCALES.some((hiddenLocale) =>
    url.includes(`/${hiddenLocale.toLowerCase()}/`)
  )
}

/**
 * Check if a URL contains a hidden locale (for SEO filtering)
 * Hidden locale URLs are always hidden from SEO in both preview and production
 */
export const isUrlHiddenFromSEO = (url?: string): boolean => {
  if (!url) return false

  // Only hide URLs on production hostname (pos.toasttab.com)
  if (!isProductionHostname()) return false

  // Always hide from SEO in both preview and production
  return PRODUCTION_HIDDEN_LOCALES.some((hiddenLocale) =>
    url.includes(`/${hiddenLocale.toLowerCase()}/`)
  )
}

/**
 * Filter localized entries to remove hidden locales (for UI elements)
 * In preview mode, all entries are visible for testing UI functionality
 * In production mode, entries with PRODUCTION_HIDDEN_LOCALES are filtered out
 */
export const filterVisibleEntries = (entries?: any[]): any[] => {
  if (!entries) return []

  return entries.filter(
    (entry) => !isLocaleHidden(entry.language) && !isUrlHidden(entry.url)
  )
}

/**
 * Filter localized entries to remove hidden locales (for SEO elements)
 * Hidden locales are always filtered from SEO in both preview and production
 * to prevent search engine indexing of unreleased content
 */
export const filterVisibleEntriesForSEO = (entries?: any[]): any[] => {
  if (!entries) return []

  return entries.filter(
    (entry) =>
      !isLocaleHiddenFromSEO(entry.language) && !isUrlHiddenFromSEO(entry.url)
  )
}
